import { Component, OnInit, HostListener } from '@angular/core';
import { ContentfulService } from 'src/app/core/contentful.service';
import { ActivatedRoute } from '@angular/router';
import { Entry } from 'contentful';
import { NavbarService } from '../../core/navbar.service';
import { SeoService } from '../../core/seo.service';

@Component({
  selector: 'app-gallery',
  templateUrl: './gallery.component.html',
  styleUrls: ['./gallery.component.css']
})
export class GalleryComponent implements OnInit {
  galleryImages: Entry<any>[] = [];
  filteredImages: Entry<any>[] = [];
  events: Entry<any>[] = [];
  selectedEvent: string = 'all';
  selectedImage: any = null;
  loading: boolean = true;
  eventSlug: string | null = null;

  constructor(
    private contentfulService: ContentfulService,
    private route: ActivatedRoute,
    public nav: NavbarService,
    private seo: SeoService
  ) { }

  ngOnInit() {
    this.nav.show();

    // Check if we're viewing a specific event's gallery
    this.route.paramMap.subscribe(params => {
      this.eventSlug = params.get('eventSlug');
      if (this.eventSlug) {
        this.loadEventGallery(this.eventSlug);
      } else {
        this.loadAllGallery();
      }
    });

    this.loadEvents();
    this.setMetadata();
  }

  loadAllGallery() {
    this.loading = true;
    Promise.all([
      this.contentfulService.getGalleryImages(),
      this.contentfulService.getEventsWithPhotos()
    ])
      .then(([galleryImages, eventsWithPhotos]) => {
        // Process eventsWithPhotos
        const eventImages = eventsWithPhotos.flatMap(event => {
          if (!event.fields.photos) return [];
          return event.fields.photos.map((photo: any) => ({
            sys: photo.sys,
            fields: {
              title: photo.fields.title || event.fields.title,
              date: event.fields.dateTime || event.fields.date,
              image: photo,
              event: event,
              photographer: photo.fields.description
            }
          }));
        });

        this.galleryImages = [...galleryImages, ...eventImages].sort((a, b) => {
          const dateA = new Date(a.fields.date || 0).getTime();
          const dateB = new Date(b.fields.date || 0).getTime();
          return dateB - dateA;
        });

        this.filteredImages = this.galleryImages;
        this.loading = false;
      })
      .catch(error => {
        console.error('Error loading gallery:', error);
        this.loading = false;
      });
  }

  loadEventGallery(eventSlug: string) {
    this.loading = true;
    this.contentfulService.getEvent(eventSlug)
      .then(event => {
        console.log('Event Gallery Debug:', { eventSlug, foundEvent: !!event, hasPhotos: event?.fields?.photos?.length });

        let eventImages: any[] = [];
        if (event && event.fields.photos) {
          eventImages = event.fields.photos
            .filter((photo: any) => photo?.fields?.file?.url) // Ensure photo has a file URL
            .map((photo: any) => ({
              sys: photo.sys,
              fields: {
                title: photo.fields.title || event.fields.title,
                date: event.fields.dateTime || event.fields.date,
                image: photo,
                event: event,
                photographer: photo.fields.description
              }
            }));
        }

        console.log('Processed Event Images:', eventImages.length);

        this.galleryImages = eventImages.sort((a, b) => {
          const dateA = new Date(a.fields.date || 0).getTime();
          const dateB = new Date(b.fields.date || 0).getTime();
          return dateB - dateA;
        });

        this.filteredImages = this.galleryImages;
        this.loading = false;
        this.selectedEvent = eventSlug;
      })
      .catch(error => {
        console.error('Error loading event gallery:', error);
        this.loading = false;
      });
  }

  loadEvents() {
    this.contentfulService.getEvents()
      .then(events => {
        this.events = events;
      });
  }

  filterByEvent(eventSlug: string) {
    this.selectedEvent = eventSlug;
    if (eventSlug === 'all') {
      this.filteredImages = this.galleryImages;
    } else {
      this.filteredImages = this.galleryImages.filter(image =>
        image.fields.event?.fields?.url === eventSlug
      );
    }
  }

  openLightbox(image: any) {
    this.selectedImage = image;
  }

  closeLightbox() {
    this.selectedImage = null;
  }

  nextImage() {
    const currentIndex = this.filteredImages.findIndex(img => img === this.selectedImage);
    if (currentIndex < this.filteredImages.length - 1) {
      this.selectedImage = this.filteredImages[currentIndex + 1];
    }
  }

  previousImage() {
    const currentIndex = this.filteredImages.findIndex(img => img === this.selectedImage);
    if (currentIndex > 0) {
      this.selectedImage = this.filteredImages[currentIndex - 1];
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (!this.selectedImage) return;
    if (event.key === 'Escape') {
      this.closeLightbox();
    } else if (event.key === 'ArrowRight') {
      this.nextImage();
    } else if (event.key === 'ArrowLeft') {
      this.previousImage();
    }
  }

  setMetadata() {
    this.seo.updateTags({
      title: 'Photo Gallery & Exhibitions',
      description: 'Explore historical photos, artifact exhibits, and community events at the Bayou Lacombe Museum in Lacombe, Louisiana.',
      url: '/gallery'
    });
  }
}
